import type { JSX, MouseEventHandler } from "react";
import { useMemo, useState } from "react";
import * as d3 from "d3";

import { calculateDetentionPeriods } from "@thalamos/pathway-logic";
import type { MhaStatusRow } from "@thalamos/pathway-logic";

import type { MhaStatusData } from "../data/mhaStatusData.js";
import { scale } from "../data/vizGeometry.js";
import {
  Annotation,
  SECTION_COLORS,
  UNKNOWN_SECTION_COLOR,
} from "./Annotations.js";
import { AssessmentLane, type AssessmentHover } from "./AssessmentLane.js";
import { AxisBottom } from "./AxisBottom.js";
import { AxisLeft } from "./AxisLeft.js";
import styles from "./scatterplot.module.css";

const MARGIN = { top: 5, right: 5, bottom: 50, left: 50 };

// Get color for a section level
const getSectionColor = (sectionLevel: number): string => {
  return SECTION_COLORS[sectionLevel] ?? UNKNOWN_SECTION_COLOR;
};

// Get section level from mha_status string
const getSectionLevel = (mhaStatus: string): number => {
  const found = scale.find(([name]) => name === mhaStatus);
  return found ? found[1] : 0;
};

type ScatterplotProps = {
  width: number;
  height: number;
  top: number;
  left: number;
  data: MhaStatusData;
  showAxisLeft: boolean;
  showAxisBottom: boolean;
  onClick: () => void;
  onMouseMove: MouseEventHandler<SVGSVGElement>;
  onCircleClick: (event: MhaStatusRow) => void;
  highlightX: number | null;
  // Show a trust "swimlane" band under the chart (zoomed single-patient view).
  showTrustBand?: boolean;
};

// Qualitative palette for the trust band — deliberately distinct from the
// section colours (Annotations.SECTION_COLORS) so the two encodings don't clash.
const TRUST_COLORS = [
  "#4f46e5",
  "#0891b2",
  "#059669",
  "#d97706",
  "#db2777",
  "#7c3aed",
  "#65a30d",
  "#b45309",
] as const;

// Cycles the palette, so a chart with more trusts than colours still renders.
const trustColour = (index: number): string =>
  TRUST_COLORS[index % TRUST_COLORS.length] ?? TRUST_COLORS[0];

// Period sections that aren't a detention — the trust ribbon skips these
// (they read as gaps). Mirrors pathway-logic's derivePatientPathway NON_DETENTION
// (Not Detained / Expired / Deceased); "Incomplete" never survives as a period
// section (calculateDetentionPeriods resolves it), so it isn't listed.
const NON_DETENTION_SECTIONS = new Set(["Not Detained", "Expired", "Deceased"]);

/** Trim the boilerplate NHS suffix so a trust name fits the band. */
function shortTrust(name: string): string {
  return name.replace(/\s+NHS\s+(Foundation\s+)?Trust$/i, "");
}

/** Short "12 Mar 2024" date, used across both tooltips. */
const fmtDate = (ms: number): string =>
  new Date(ms).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

/** Which edge of the plot area an event fell off, or 0 when it is in range. */
type OffScale = -1 | 0 | 1;

/**
 * Hold an event's x inside the plot area. The axis range is not guaranteed to
 * cover every event — a hand-entered clinical timestamp can land far outside it —
 * and an unheld mark is drawn beyond the SVG viewport, where it disappears
 * entirely instead of being visibly clipped.
 */
export function pinToAxis(
  x: number,
  boundsWidth: number,
): { x: number; offScale: OffScale } {
  if (x < 0) return { x: 0, offScale: -1 };
  if (x > boundsWidth) return { x: boundsWidth, offScale: 1 };
  return { x, offScale: 0 };
}

/**
 * Triangle with its apex on the boundary the event fell off and its base inside
 * the plot area, so the whole mark stays inside the viewport.
 */
function offScaleMarkPath(
  x: number,
  y: number,
  radius: number,
  offScale: OffScale,
): string {
  const base = x - offScale * radius * 1.6;
  return `M ${String(x)},${String(y)} L ${String(base)},${String(y - radius)} L ${String(base)},${String(y + radius)} Z`;
}

/**
 * One plotted event: a circle in range, a triangle pinned to the edge otherwise.
 * Presentation attributes and pointer handlers sit on the wrapping `g` so both
 * shapes share them.
 */
const EventMark = ({
  x,
  y,
  radius,
  fill,
  offScale,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  x: number;
  y: number;
  radius: number;
  fill: string;
  offScale: OffScale;
  onClick: MouseEventHandler<SVGGElement>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}): JSX.Element => (
  <g
    stroke="white"
    strokeWidth={1}
    fill={fill}
    style={{ cursor: "pointer" }}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {offScale === 0 ? (
      <circle r={radius} cx={x} cy={y} />
    ) : (
      <path d={offScaleMarkPath(x, y, radius, offScale)} />
    )}
  </g>
);

type InteractionData = {
  xPos: number;
  yPos: number;
  text: string;
  event: MhaStatusRow;
  offScale: OffScale;
};

const TOOLTIP_WIDTH = 210;

const Tooltip = ({
  interactionData,
  boundsWidth,
}: {
  interactionData: InteractionData | null;
  boundsWidth: number;
}) => {
  if (!interactionData) return null;
  const { xPos, yPos, event, offScale } = interactionData;
  // Flip to the left of the dot when it's near the right edge, so the tooltip
  // stays inside the chart (and out from under the sidebar).
  const flip = xPos + TOOLTIP_WIDTH + 12 > boundsWidth;
  const left = flip ? xPos - TOOLTIP_WIDTH - 8 : xPos + 8;
  const date = fmtDate(new Date(event.effective_date_time).getTime());
  return (
    <div
      className={styles.tooltip}
      style={{
        left,
        top: yPos,
        display: "flex",
        flexDirection: "column",
        width: TOOLTIP_WIDTH,
        zIndex: 9999,
      }}
    >
      <span>Date: {date}</span>
      {offScale !== 0 && (
        <span style={{ fontStyle: "italic" }}>
          Outside the chart&apos;s date range — pinned to the{" "}
          {offScale < 0 ? "left" : "right"} edge
        </span>
      )}
      <span>Event Type: {event.event_type}</span>
      <span>MHA Status: {event.mha_status}</span>
      {event.trust_name && <span>Trust: {event.trust_name}</span>}
      {event.hospital_name && <span>Hospital: {event.hospital_name}</span>}
    </div>
  );
};

// One detention period cell in the ribbon (the sub-cells that make the "split
// by detention period" visual cue). Carries its own trust/colour so a
// mid-detention transfer shows as a colour change within the episode.
type RibbonPeriod = {
  start: number;
  end: number;
  section: string;
  status: string;
  durationDays: number;
  trust: string;
  color: string;
};

// A consecutive run under one trust within an episode — just for the label.
type TrustRun = { trust: string; color: string; start: number; end: number };

// One continuous detention episode (broken only at a discharge, NOT at a trust
// transfer): the patient stayed detained throughout, possibly moving trust.
type RibbonEpisode = {
  start: number;
  end: number;
  /** Section pathway across the episode, consecutive repeats collapsed. */
  sections: string[];
  /** Trust journey across the episode, consecutive repeats collapsed. */
  trusts: string[];
  trustRuns: TrustRun[];
  periods: RibbonPeriod[];
};

// What a hovered ribbon cell shows: the whole detention episode (section
// pathway, trust journey, span) plus the specific period under the cursor —
// so a detention that transfers trust shares one episode-level hover.
type BandInteraction = {
  xPos: number;
  episodeStart: number;
  episodeEnd: number;
  sections: string[];
  trusts: string[];
  period: RibbonPeriod;
};

const BAND_TOOLTIP_WIDTH = 240;

const BandTooltip = ({
  band,
  boundsWidth,
}: {
  band: BandInteraction | null;
  boundsWidth: number;
}) => {
  if (!band) return null;
  // Flip left near the right edge so it stays inside the chart / clear of the sidebar.
  const flip = band.xPos + BAND_TOOLTIP_WIDTH + 12 > boundsWidth;
  const left = flip ? band.xPos - BAND_TOOLTIP_WIDTH - 8 : band.xPos + 8;
  const { period } = band;
  const transferred = band.trusts.length > 1;
  return (
    <div
      className={styles.tooltip}
      style={{
        left,
        top: 0,
        // Override the class's translateY(-50%) so it hangs below the ribbon
        // (which sits just above this overlay), not up over it.
        transform: "none",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: BAND_TOOLTIP_WIDTH,
        zIndex: 9999,
      }}
    >
      {/* Full name of the trust the hovered cell is under. */}
      <span style={{ fontWeight: 600 }}>{period.trust}</span>
      {/* The one continuous detention this cell belongs to. */}
      <span>
        {fmtDate(band.episodeStart)} – {fmtDate(band.episodeEnd)}
      </span>
      <span style={{ opacity: 0.85 }}>
        Detention: {band.sections.join(" → ")}
      </span>
      {transferred && (
        <span style={{ opacity: 0.85 }}>
          Transfer: {band.trusts.map(shortTrust).join(" → ")}
        </span>
      )}
      <span
        style={{
          marginTop: 4,
          paddingTop: 4,
          borderTop: "1px solid rgba(255,255,255,0.25)",
        }}
      >
        This period: {period.section} ({period.status})
      </span>
      <span style={{ opacity: 0.85 }}>
        {fmtDate(period.start)} – {fmtDate(period.end)} · {period.durationDays}d
      </span>
    </div>
  );
};

const ASSESSMENT_TOOLTIP_WIDTH = 190;

/** Human-readable outcome — the lane's marks carry no outcome encoding at all. */
const END_REASON_LABELS: Record<string, string> = {
  detained: "Detained",
  "no-application": "No application",
  "no-med-rec": "No med rec",
  "closed-other": "Closed, no reason",
  lapsed: "Lapsed",
  open: "Open",
  deceased: "Died",
};

/** Short "12 Mar" — the year lives on the range's end, so the start needn't repeat it. */
const fmtDayMonth = (ms: number): string =>
  new Date(ms).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

const AssessmentTooltip = ({
  hover,
  boundsWidth,
}: {
  hover: AssessmentHover | null;
  boundsWidth: number;
}) => {
  if (!hover) return null;
  const { assessment: a } = hover;
  const flip = hover.xPos + ASSESSMENT_TOOLTIP_WIDTH + 12 > boundsWidth;
  const left = flip
    ? hover.xPos - ASSESSMENT_TOOLTIP_WIDTH - 8
    : hover.xPos + 8;
  const outcome = END_REASON_LABELS[a.endReason] ?? a.endReason;
  // Anything the reader would otherwise take at face value: a detention tied to
  // this assessment only by timing, an outcome with no examination behind it, or a
  // start whose time of day was never recorded.
  const caveat =
    a.detentionLinkage === "inferred"
      ? "timing-matched"
      : a.kind === "marker"
        ? "no examination recorded"
        : a.intendedSectionSource === "med-rec"
          ? "section from rec"
          : a.startPrecision === "day"
            ? "date only"
            : null;
  return (
    <div
      className={styles.tooltip}
      style={{
        left,
        // Anchored to the bottom of the plot area, which is where the lane sits —
        // the class's own vertical centring would put it a chart-height away from
        // the mark being hovered.
        bottom: 4,
        transform: "none",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        width: ASSESSMENT_TOOLTIP_WIDTH,
        fontSize: 11,
        lineHeight: 1.35,
        padding: "5px 7px",
        zIndex: 9999,
      }}
    >
      <span>
        {a.startAt !== null
          ? `${fmtDayMonth(new Date(a.startAt).getTime())} – ${fmtDate(new Date(a.endAt).getTime())}`
          : fmtDate(new Date(a.endAt).getTime())}
        {a.durationDays !== null && ` · ${a.durationDays.toFixed(1)}d`}
      </span>
      {/* Lead with what the assessment was FOR, since that is knowable whatever
          happened, then what became of it. The section is only stated once when the
          two agree. */}
      <span style={{ fontWeight: 600 }}>
        {a.intendedSection ?? "Section unknown"}
        {" · "}
        {outcome}
        {a.outcomeSection !== null &&
          a.outcomeSection !== a.intendedSection &&
          ` → ${a.outcomeSection}`}
      </span>
      <span style={{ opacity: 0.75 }}>
        {a.medRecCount} rec{a.medRecCount === 1 ? "" : "s"} ·{" "}
        {a.applicationCount} app{a.applicationCount === 1 ? "" : "s"}
        {caveat !== null && ` · ${caveat}`}
      </span>
      {/* The operator's own words for not proceeding — the one thing here that no
          derived field can reconstruct. */}
      {a.closedReasonRaw !== null && a.closedReasonRaw !== "" && (
        <span style={{ opacity: 0.75 }}>{a.closedReasonRaw}</span>
      )}
    </div>
  );
};

export const Scatterplot = ({
  width,
  height,
  data,
  top,
  left,
  showAxisLeft,
  showAxisBottom,
  onClick,
  onMouseMove,
  onCircleClick,
  highlightX,
  showTrustBand = false,
}: ScatterplotProps) => {
  const [interactionData, setInteractionData] =
    useState<InteractionData | null>(null);
  const [bandInteraction, setBandInteraction] =
    useState<BandInteraction | null>(null);

  const [assessmentHover, setAssessmentHover] =
    useState<AssessmentHover | null>(null);

  // Trust swimlane sits as a ribbon along the top; reserve space for it so the
  // section chart drops below (only in the zoomed view that shows the band).
  const TRUST_BAND_H = 16;
  const trustBandOffset = showTrustBand ? TRUST_BAND_H + 8 : 0;

  // Assessment lane sits UNDER the section plot, next to the low end of the
  // scale, so an assessment and the 136 or holding power it ran inside are only a
  // few pixels apart.
  const assessments = data.patients[0]?.assessments ?? [];
  // The trust band is drawn only in the single-patient view, so it doubles as
  // "there is room here": the lane takes a height its glyphs can be read at,
  // rather than the few pixels a cell in the grid can spare.
  const laneHeight = showTrustBand ? 16 : 7;
  const assessmentLaneOffset = assessments.length > 0 ? laneHeight + 4 : 0;

  const boundsWidth = width - MARGIN.right - (showAxisLeft ? MARGIN.left : 0);
  const boundsHeight =
    height -
    MARGIN.top -
    trustBandOffset -
    assessmentLaneOffset -
    (showAxisBottom ? MARGIN.bottom : 0);

  const [minx, maxx] = data.dateRange;

  // Scales - domain goes to 9 to accommodate Section 17A at level 8
  const yScale = d3.scaleLinear().domain([0, 9]).range([boundsHeight, 0]);

  const xScale = d3
    .scaleLinear()
    .domain([minx.getTime(), maxx.getTime()])
    .range([0, boundsWidth]);

  // Trust swimlane, built from the authoritative detention periods (not raw
  // events) but sampled the SAME way the section line is, so the two never
  // disagree. The source periods can overlap and arrive out of order (backdated
  // `initial_start`, duplicate expiry rows), so we don't iterate them directly:
  // we sweep their boundary instants and, in each interval, take the FIRST
  // (earliest-effective) covering period — exactly `generateVisualisationFromPeriods`'s
  // day-sampling rule (vizGeometry.ts). That shadows the duplicates the line
  // shadows, so a spurious re-detention never appears.
  //
  // Adjacent intervals of the same section+trust merge into one cell (the
  // "split by detention period" cue is the gap between cells). Cells that touch
  // in time form one episode = one continuous detention, broken only where a
  // non-detaining stretch (Not Detained / Expired / Deceased / no provider)
  // leaves a real time gap — NOT at a trust transfer, so a detention that moves
  // trust mid-stay stays a single episode with one shared hover. A stable
  // trust→colour map keeps a trust's colour consistent; memoised on the (stable)
  // focus patient so it survives mouse-move re-renders.
  const ribbonEpisodes = useMemo<RibbonEpisode[]>(() => {
    const patient = data.patients[0];
    if (!showTrustBand || !patient) return [];
    const periods = calculateDetentionPeriods(patient.entries);

    const bounds = new Set<number>();
    for (const p of periods) {
      bounds.add(p.startDate.getTime());
      bounds.add(p.endDate.getTime());
    }
    const times = [...bounds].sort((a, b) => a - b);

    // Sweep the intervals into detention cells, merging same section+trust runs.
    const colorByTrust = new Map<string, string>();
    const cells: RibbonPeriod[] = [];
    for (const [i, a] of times.entries()) {
      const b = times[i + 1];
      // The final boundary opens no interval.
      if (b === undefined) continue;
      const mid = (a + b) / 2;
      const active = periods.find(
        (p) => mid >= p.startDate.getTime() && mid <= p.endDate.getTime(),
      );
      if (!active || NON_DETENTION_SECTIONS.has(active.section)) continue;
      const trust = active.trustName ?? [...active.trusts][0] ?? null;
      if (!trust) continue;
      const last = cells[cells.length - 1];
      if (
        last &&
        last.end === a &&
        last.trust === trust &&
        last.section === active.section
      ) {
        last.end = b;
        continue;
      }
      const color = colorByTrust.get(trust) ?? trustColour(colorByTrust.size);
      colorByTrust.set(trust, color);
      cells.push({
        start: a,
        end: b,
        section: active.section,
        status: active.completionStatus,
        durationDays: 0,
        trust,
        color,
      });
    }
    for (const c of cells) {
      c.durationDays = Math.max(0, Math.round((c.end - c.start) / 86_400_000));
    }

    // Contiguous cells (touching in time) form one continuous-detention episode.
    const episodes: RibbonEpisode[] = [];
    let current: RibbonEpisode | null = null;
    for (const c of cells) {
      if (!current || c.start !== current.end) {
        current = {
          start: c.start,
          end: c.end,
          sections: [c.section],
          trusts: [c.trust],
          trustRuns: [],
          periods: [c],
        };
        episodes.push(current);
      } else {
        current.periods.push(c);
        current.end = c.end;
        // Consecutive-collapse: a genuine return still shows (CTO recall reads
        // Section 17A → 2 → 3 → Section 17A, not a globally de-duped variant).
        if (current.sections[current.sections.length - 1] !== c.section) {
          current.sections.push(c.section);
        }
        if (current.trusts[current.trusts.length - 1] !== c.trust) {
          current.trusts.push(c.trust);
        }
      }
    }
    // Collapse each episode's cells into consecutive same-trust runs (labels).
    for (const ep of episodes) {
      for (const c of ep.periods) {
        const last = ep.trustRuns[ep.trustRuns.length - 1];
        if (last && last.trust === c.trust) last.end = c.end;
        else
          ep.trustRuns.push({
            trust: c.trust,
            color: c.color,
            start: c.start,
            end: c.end,
          });
      }
    }
    return episodes;
  }, [showTrustBand, data.patients]);

  const lines: [string, string | null][] = [];
  let circles: JSX.Element[] = [];
  const deathLines: JSX.Element[] = [];

  for (const patient of data.patients) {
    const lineBuilder = d3
      .line<MhaStatusData["patients"][0]["line"][0]>()
      .x((d) => xScale(d.x))
      .y((d) => yScale(d.y));
    lines.push(["black", lineBuilder(patient.line)]);

    // DEATH!!!! vertical line
    if (patient.deceasedDateTime) {
      const deathX = xScale(patient.deceasedDateTime.getTime());
      if (deathX >= 0 && deathX <= boundsWidth) {
        deathLines.push(
          <line
            key={`death-${patient.id}`}
            x1={deathX}
            x2={deathX}
            y1={0}
            y2={boundsHeight}
            stroke="#dc2626"
            strokeWidth={2}
            opacity={0.7}
            strokeDasharray="4,2"
          />,
        );
      }
    }

    circles = circles.concat(
      patient.circles.map((d, i) => {
        const sectionLevel = getSectionLevel(d.event.mha_status);
        const fillColor = getSectionColor(sectionLevel);
        const radius = Math.log10(width) * 1.5;
        const { x: cx, offScale } = pinToAxis(xScale(d.x), boundsWidth);
        const cy = yScale(d.y);

        return (
          <EventMark
            key={`${patient.id}-${String(i)}`}
            x={cx}
            y={cy}
            radius={radius}
            fill={fillColor}
            offScale={offScale}
            onClick={(e) => {
              e.stopPropagation();
              onCircleClick(d.event);
            }}
            onMouseEnter={() => {
              setInteractionData({
                xPos: cx,
                yPos: cy,
                text: d.event.event_type,
                event: d.event,
                offScale,
              });
            }}
            onMouseLeave={() => {
              setInteractionData(null);
            }}
          />
        );
      }),
    );
  }

  // Build the line for moving average

  return (
    <>
      <svg
        width={width}
        height={height}
        style={{ position: "absolute", top, left }}
        onClick={onClick}
        onMouseMove={onMouseMove}
      >
        <g
          width={boundsWidth}
          height={boundsHeight}
          transform={`translate(${[
            showAxisLeft ? MARGIN.left : 0,
            MARGIN.top,
          ].join(",")})`}
        >
          {/* Trust swimlane. One cell per detention period (gaps between cells
              split the periods; a blank run is time out of detention). Cells are
              coloured by trust, so a transfer mid-detention shows as a colour
              change with no gap. Hover is episode-scoped: any cell of one
              continuous detention shows the same pathway + trust journey. Labels
              sit once per consecutive trust run. */}
          {showTrustBand && ribbonEpisodes.length > 0 && (
            <g>
              {ribbonEpisodes.map((ep, ei) => (
                <g key={ei}>
                  {ep.periods.map((p, pi) => {
                    const px1 = Math.max(
                      0,
                      Math.min(boundsWidth, xScale(p.start)),
                    );
                    const px2 = Math.max(
                      0,
                      Math.min(boundsWidth, xScale(p.end)),
                    );
                    // Inset 1px each side → a 2px gap between adjacent cells.
                    const w = Math.max(1, px2 - px1 - 2);
                    return (
                      <rect
                        key={pi}
                        x={px1 + 1}
                        y={0}
                        width={w}
                        height={TRUST_BAND_H}
                        rx={2}
                        fill={p.color}
                        opacity={0.85}
                        onMouseEnter={() => {
                          setBandInteraction({
                            xPos: px1,
                            episodeStart: ep.start,
                            episodeEnd: ep.end,
                            sections: ep.sections,
                            trusts: ep.trusts,
                            period: p,
                          });
                        }}
                        onMouseLeave={() => {
                          setBandInteraction(null);
                        }}
                      />
                    );
                  })}
                  {ep.trustRuns.map((run, ri) => {
                    const rx1 = Math.max(
                      0,
                      Math.min(boundsWidth, xScale(run.start)),
                    );
                    const rx2 = Math.max(
                      0,
                      Math.min(boundsWidth, xScale(run.end)),
                    );
                    if (rx2 - rx1 <= 34) return null;
                    const label = shortTrust(run.trust);
                    const maxChars = Math.floor((rx2 - rx1) / 6);
                    return (
                      <text
                        key={`r${String(ri)}`}
                        x={rx1 + 4}
                        y={12}
                        fontSize={9}
                        fill="white"
                        pointerEvents="none"
                      >
                        {label.length > maxChars
                          ? label.slice(0, Math.max(1, maxChars - 1)) + "…"
                          : label}
                      </text>
                    );
                  })}
                </g>
              ))}
            </g>
          )}

          {/* Section chart, dropped below the trust ribbon */}
          <g transform={`translate(0, ${String(trustBandOffset)})`}>
            {/* Annotation */}
            <Annotation yScale={yScale} boundsWidth={boundsWidth} />

            {/* Y axis */}
            {showAxisLeft && <AxisLeft yScale={yScale} />}

            {highlightX !== null && (
              <line
                x1={highlightX}
                x2={highlightX}
                y1={0}
                y2={boundsHeight}
                stroke="darkgrey"
                strokeWidth={0.5}
                shapeRendering={"crispEdges"}
                strokeDasharray="2,2"
              />
            )}

            {/* Assessment lane, immediately below the plot area. */}
            {assessmentLaneOffset > 0 && (
              <g transform={`translate(0, ${String(boundsHeight + 4)})`}>
                <AssessmentLane
                  assessments={assessments}
                  xScale={xScale}
                  boundsWidth={boundsWidth}
                  height={laneHeight}
                  onHover={setAssessmentHover}
                />
              </g>
            )}

            {/* X axis, use an additional translation to appear at the bottom */}
            <g
              transform={`translate(0, ${String(boundsHeight + assessmentLaneOffset)})`}
            >
              {showAxisBottom && (
                <AxisBottom xScale={xScale} pixelsPerTick={100} />
              )}
            </g>

            {/* DEATH!!!! lines */}
            {deathLines}

            {/* Circles */}
            {circles}

            {/* Line Chart */}
            {lines.map(([, l], idx) => (
              <path
                key={idx}
                d={l ?? undefined}
                opacity={0.6}
                stroke="#64748b"
                fill="none"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}
          </g>
        </g>
      </svg>

      <div
        style={{
          width: boundsWidth, // the width of the chart area excluding axes = width - left margin
          height: boundsHeight,
          position: "absolute",
          top,
          left,
          pointerEvents: "none",
          marginLeft: showAxisLeft ? MARGIN.left : 0,
          marginTop: MARGIN.top + trustBandOffset,
        }}
      >
        <Tooltip interactionData={interactionData} boundsWidth={boundsWidth} />
        <BandTooltip band={bandInteraction} boundsWidth={boundsWidth} />
        <AssessmentTooltip hover={assessmentHover} boundsWidth={boundsWidth} />
      </div>
    </>
  );
};
